import React, { useEffect, useCallback, useState, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { routes } from "router"
import styles from "./index.module.css"
import { Card, Flex, Typography, Button, Form, Modal } from "antd"
import { CheckCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import AddressIcon from "../../../../assets/icons/Address.svg"
import CalendarIcon from "../../../../assets/icons/calendar.svg"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { useSharedWaiverData } from "./hooks/useSharedWaiverData"
import { useParentForm } from "./hooks/useParentForm"
import { WaiverOptions } from "./components/WaiverOptions"
import { WaiverDetails } from "./components/WaiverDetails"
import { ParentGuardianForm } from "./components/ParentGuardianForm"
import { MinorGuestForm } from "./components/MinorGuestForm"
import bgMan from "assets/imagesV2/logo_man.svg"
import bgWoman from "assets/imagesV2/logo_woman.svg"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import { useDispatch, useSelector } from "react-redux"
import { WAIVER_SUBMISSION_STATUS } from "constants/waiver"
import { postWaiverSubmission, checkSubmissionsAvailable } from "store/digital-waiver"
import { getBooking } from "store/bookings"
import { toaster } from "components/common/Toast"
import { SignDocumentModal } from "pagesV2/pro/Embedded/BookingConfirmation/modal/SignDocument"

const { Title, Text } = Typography

export default function SharedWaivers() {
  const { t } = useTranslation()
  const { id, uuid: urlUuid } = useParams()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const dispatch = useDispatch()
  const { loading: postLoading } = useSelector((store) => store?.digitalWaiver) || {}
  const guestData = useSelector((state) => state.digitalWaiver.waiverSubmissionsByTripBooking.guest_data)
  const bookingInfo = useSelector((state) => state.bookings.booking)

  const { waiver, loading, status, waiverSubmissionId: existingWaiverSubmissionId } = useSharedWaiverData(id)

  const { selected, setSelected, parentData, setParentData, form } = useParentForm()
  const guardianForm = Form.useForm()[0]
  const logoSrc = theme === themes.sportswoman ? bgWoman : bgMan
  // Reset forms when switching between adult and guardian
  React.useEffect(() => {
    if (selected === "adult" && form) {
      form.resetFields()
      setShowAdultForm(false)
    } else if (selected === "guardian" && guardianForm) {
      guardianForm.resetFields()
      setShowGuardianForm(false)
    }
    setIsFormValid(false)
  }, [selected])
  const [isFormValid, setIsFormValid] = useState(false)
  const [showAdultForm, setShowAdultForm] = useState(false)
  const [showGuardianForm, setShowGuardianForm] = useState(false)
  const [showSignDocumentModal, setShowSignDocumentModal] = useState(false)
  const [waiverSubmissionId, setWaiverSubmissionId] = useState(null)
  const [pendingDocuments, setPendingDocuments] = useState([])
  const [bookingUuid, setBookingUuid] = useState(null)

  // Use UUID from URL params directly (available on /client/trip_booking/:uuid/shared_waiver/:id)
  useEffect(() => {
    if (urlUuid) {
      dispatch(checkSubmissionsAvailable(urlUuid))
      dispatch(getBooking(urlUuid, true))
    }
  }, [urlUuid, dispatch])

  useEffect(() => {
    if (status === "signed") {
      window.history.pushState(null, "", window.location.href)
      const handlePopState = () => window.history.pushState(null, "", window.location.href)
      window.addEventListener("popstate", handlePopState)
      return () => window.removeEventListener("popstate", handlePopState)
    }
  }, [status])

  // Extract trip booking UUID from waiver data when available, and fetch guest data
  useEffect(() => {
    const tripUuid = urlUuid || waiver?.uuid
    if (tripUuid) {
      setBookingUuid(tripUuid)
      if (!urlUuid) {
        // Only call here if urlUuid not available (fallback for /client/shared_waivers/:id route)
        dispatch(checkSubmissionsAvailable(tripUuid))
      }
    }
  }, [urlUuid, waiver?.uuid, dispatch])

  const pendingChildCount = useMemo(
    () =>
      (guestData || []).filter(
        (g) =>
          g.participant_type === "Child" &&
          g.status === WAIVER_SUBMISSION_STATUS.UNSIGNED &&
          (!g.pending_submission || g.pending_submission.length === 0)
      ).length,
    [guestData]
  )

  // Auto-open sign modal on page load/refresh if a submission is already pending
  useEffect(() => {
    if (status === WAIVER_SUBMISSION_STATUS.PENDING || status === WAIVER_SUBMISSION_STATUS.PARTIALLY_SIGNED) {
      const docs = status === WAIVER_SUBMISSION_STATUS.PARTIALLY_SIGNED
        ? (waiver?.pending_submissions || [])
        : (waiver?.waivers || [])
      setWaiverSubmissionId(existingWaiverSubmissionId)
      setPendingDocuments(docs)
      setShowSignDocumentModal(true)
    }
  }, [status, existingWaiverSubmissionId])

  // Check if adult form is valid
  const checkAdultFormValidity = useCallback(() => {
    if (selected !== "adult" || !form) return false

    const values = form.getFieldsValue()
    const requiredFields = ["firstName", "lastName", "email", "dob", "phone"]

    return requiredFields.every((field) => {
      const value = values[field]
      if (field === "phone") {
        return value?.fullNumber?.trim()
      }
      return value?.toString().trim()
    })
  }, [selected, form])

  // Check if guardian form is valid
  const checkGuardianFormValidity = useCallback(() => {
    if (selected !== "guardian" || !guardianForm) return false

    // Check guardian personal details
    if (form) {
      const parentValues = form.getFieldsValue()
      const requiredFields = ["firstName", "lastName", "email", "dob", "phone", "relation"]
      const parentValid = requiredFields.every((field) => {
        const value = parentValues[field]
        if (field === "phone") return value?.fullNumber?.trim()
        return value?.toString().trim()
      })
      if (!parentValid) return false
    }

    const values = guardianForm.getFieldsValue()
    const minors = values.minors || []

    if (minors.length === 0) return false

    return minors.every((minor) => {
      return minor.fullName?.trim() && minor.dateOfBirth
    })
  }, [selected, guardianForm, form])

  // Update form validity when selection or form values change
  useEffect(() => {
    let valid = false
    if (selected === "adult") {
      // Enable next button immediately when adult is selected (before showing form)
      valid = !showAdultForm
    } else if (selected === "guardian") {
      // Enable next button immediately when guardian is selected (before showing form)
      valid = !showGuardianForm
    }
    setIsFormValid(valid)
  }, [selected, showAdultForm, showGuardianForm])

  // Update form validity when adult form values change
  useEffect(() => {
    if (selected === "adult" && showAdultForm) {
      const valid = checkAdultFormValidity()
      setIsFormValid(valid)
    }
  }, [selected, showAdultForm, checkAdultFormValidity])

  // Update form validity when guardian form values change
  useEffect(() => {
    if (selected === "guardian" && showGuardianForm) {
      const valid = checkGuardianFormValidity()
      setIsFormValid(valid)
    }
  }, [selected, showGuardianForm, checkGuardianFormValidity])

  const handleNext = useCallback(async () => {
    if (selected === "adult" && !showAdultForm) {
      setShowAdultForm(true)
      setIsFormValid(false)
    } else if (selected === "guardian" && !showGuardianForm) {
      setShowGuardianForm(true)
      setIsFormValid(false)
    } else if (selected === "adult" && showAdultForm) {
      try {
        const values = await form.validateFields()
        const formData = new FormData()
        formData.append("waiver_submission[shared_waiver_id]", id)
        if (waiver?.trip_booking_id) {
          formData.append("waiver_submission[trip_booking_id]", waiver.trip_booking_id)
        }
        formData.append("waiver_submission[submission_for]", "adult")
        formData.append("waiver_submission[waiver_participants_attributes][0][participant_type]", "adult")
        formData.append("waiver_submission[waiver_participants_attributes][0][first_name]", values.firstName || "")
        formData.append("waiver_submission[waiver_participants_attributes][0][last_name]", values.lastName || "")
        formData.append("waiver_submission[waiver_participants_attributes][0][email]", values.email || "")
        formData.append("waiver_submission[waiver_participants_attributes][0][phone_number]", values.phone?.fullNumber || "")
        const formattedDate = values.dob ? new Date(values.dob).toLocaleDateString("en-GB").replaceAll("/", "-") : ""
        formData.append("waiver_submission[waiver_participants_attributes][0][birthdate]", formattedDate)
        dispatch(postWaiverSubmission(formData)).then((res) => {
          toaster.success(res?.message)
          const docs = res?.data?.pending_waiver_submissions || []
          setWaiverSubmissionId(res?.data?.id)
          setPendingDocuments(docs)
          setShowSignDocumentModal(true)
        })
      } catch {
        // Validation errors shown by AntD
      }
    } else if (selected === "guardian" && showGuardianForm) {
      try {
        const [guardianValues] = await Promise.all([form.validateFields(), guardianForm.validateFields()])
        const values = guardianForm.getFieldsValue()
        const minors = values.minors || []
        const formData = new FormData()
        formData.append("waiver_submission[shared_waiver_id]", id)
        if (waiver?.trip_booking_id) {
          formData.append("waiver_submission[trip_booking_id]", waiver.trip_booking_id)
        }
        formData.append("waiver_submission[submission_for]", "minor")
        formData.append("waiver_submission[guardian_first_name]", guardianValues.firstName || "")
        formData.append("waiver_submission[guardian_last_name]", guardianValues.lastName || "")
        formData.append("waiver_submission[guardian_email]", guardianValues.email || "")
        formData.append("waiver_submission[guardian_phone_number]", guardianValues.phone?.fullNumber || "")
        const formattedGuardianDate = guardianValues.dob ? new Date(guardianValues.dob).toLocaleDateString("en-GB").replaceAll("/", "-") : ""
        formData.append("waiver_submission[guardian_birthdate]", formattedGuardianDate)
        formData.append("waiver_submission[relation]", guardianValues.relation || "")
        minors.forEach((minor, index) => {
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][participant_type]`, "minor")
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][full_name]`, minor.fullName || "")
          const formattedDate = minor.dateOfBirth ? new Date(minor.dateOfBirth).toLocaleDateString("en-GB").replaceAll("/", "-") : ""
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][birthdate]`, formattedDate)
        })
        dispatch(postWaiverSubmission(formData)).then((res) => {
          toaster.success(res?.message)
          const docs = res?.data?.pending_waiver_submissions || []
          setWaiverSubmissionId(res?.data?.id)
          setPendingDocuments(docs)
          setShowSignDocumentModal(true)
        })
      } catch {
        // Validation errors shown by AntD
      }
    }
  }, [selected, showAdultForm, showGuardianForm, form, guardianForm, id, dispatch, waiver])

  if (status === "signed") {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "100vh", background: "#f5f5f5" }}>
        <Modal
          open
          footer={null}
          closable={false}
          maskClosable={false}
          centered
          width={440}
          styles={{ mask: { background: "transparent" } }}
        >
          <div style={{ textAlign: "center" }}>
            <Flex align="center" justify="center" style={{ width: 56, height: 56, borderRadius: "50%", background: "#e6f4ff", margin: "0 auto 16px" }}>
              <CheckCircleOutlined style={{ fontSize: 24, color: "#1677ff" }} />
            </Flex>
            <Title level={4} style={{ marginBottom: 4 }}>
              Waiver Already Signed
            </Title>
            <Text type="secondary">
              You have already signed this waiver.
            </Text>
          </div>
        </Modal>
      </Flex>
    )
  }

  if (showSignDocumentModal) {
    return (
      <SignDocumentModal
        fullScreen
        showPageHeader
        open={true}
        uuid={bookingUuid}
        waiverSubmissionId={waiverSubmissionId}
        pendingSubmission={pendingDocuments}
        bookingWaivers={pendingDocuments}
        onCancel={() => {
          navigate(routes.clientWaiverThankYou())
        }}
      />
    )
  }

  return (
    <Flex gap={10} vertical className={`${styles.container} flex-1`} align="center" justify="center">
      <div className="w-full p-m bg-white">
        <img src={logoSrc} alt="Digital Sportsman" className={styles.poweredLogo} />
      </div>
      <Flex className={styles.card} align="center" vertical>
        <Card>
          <Title level={3} className={styles.title}>
            {t("digital_waiver_and_contracts.waiver_form")}
          </Title>

          <WaiverDetails
            title={bookingInfo?.trip?.title || ""}
            date={bookingInfo?.booked_date || ""}
            location={bookingInfo?.trip?.address || ""}
            calendarIcon={CalendarIcon}
            addressIcon={AddressIcon}
          />

          <div className={`mt-4xl mb-s ${styles.subTitle}`}>{t("embedded.booking_confirmation.who_is_participation")}</div>

          <WaiverOptions selected={selected} onSelect={setSelected} className={`mb-4xl`} />

          {selected === "adult" && showAdultForm && (
            <ParentGuardianForm
              form={form}
              parentData={parentData}
              setParentData={setParentData}
              t={t}
              validatePhoneNumber={validatePhoneNumber}
              setIsFormValid={setIsFormValid}
            />
          )}
          {selected === "guardian" && showGuardianForm && (
            <>
              <ParentGuardianForm
                form={form}
                parentData={parentData}
                setParentData={setParentData}
                t={t}
                validatePhoneNumber={validatePhoneNumber}
                setIsFormValid={() => setIsFormValid(checkGuardianFormValidity())}
                showRelation={true}
              />
              <MinorGuestForm t={t} form={guardianForm} setIsFormValid={() => setIsFormValid(checkGuardianFormValidity())} childCount={pendingChildCount} />
            </>
          )}

          <Flex justify="center" className={styles.buttonContainer}>
            <Button color="primary" variant="solid" size="large" className={styles.nextButton} onClick={handleNext} disabled={!isFormValid || postLoading} loading={postLoading}>
              {t("embedded.booking_confirmation.next")}
            </Button>
          </Flex>
        </Card>
      </Flex>
    </Flex>
  )
}
